//* Libraries imports
import { useState, useMemo } from "react";
import {
  format,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Send,
  MoreVertical,
  EyeIcon,
  Trash2Icon,
  Edit,
  FileText,
  CheckCircle,
} from "lucide-react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { toast } from "sonner";

//* Hooks Imports
import { useIndexSales } from "@hooks/sales/queries/useIndexSales";
import { useDeleteSale } from "@hooks/sales/mutations/useDeleteSale";
import { useGenerateSaleToken } from "@hooks/sales/mutations/useGenerateSaleToken";

//* UI Components
import { DataTable } from "@components/ui";
import type { Column } from "@components/ui/DataTable";
import { SalesFilters } from "./SalesFilters";
import { SalesReceiptModal } from "./components/SalesReceiptModal";

//* Types
import { PaymentStatus } from "@/schemas/payment";
import { type ISaleFilters } from "@/schemas/sales";

//* Helpers Imports
import { getStatusLabel, getStatusColor } from "@helpers/paymentHelper";
import { getSalePrice } from "@helpers/saleHelper";

//* Utils Imports
import { formatDateTime } from "@/utils/formatDate";
import { formatCurrency } from "@/utils/currencyUtils";

function SalesCard({
  item,
  handleSendPayment,
  handleDelete,
  handleViewReceipt,
}: {
  item: any;
  handleSendPayment: (item: any) => void;
  handleDelete: (id: string) => void;
  handleViewReceipt: (item: any) => void;
}) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className="flex border border-[#EDF2F6] rounded-[14px] bg-[#FCFCFC] overflow-hidden hover:border-blue-200 transition-colors shadow-sm w-full">
      <div className="flex flex-col items-center justify-center p-4 bg-[#F8FAFC] border-r border-[#EDF2F6] min-w-[120px] gap-5">
        <div className="size-16 rounded-lg overflow-hidden border border-blue-100 shadow-sm bg-white">
          <img
            src={item.avatarUrl}
            alt={item.clientName}
            className="size-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                `https://ui-avatars.com/api/?name=${encodeURIComponent(item.clientName)}&background=random`;
            }}
          />
        </div>
        <div className="flex flex-col items-center gap-0.5 bg-gray-200 p-4 rounded-md ">
          <span className="text-[13px] font-bold text-gray-400 border-b border-gray-200 pb-0.5 w-full text-center">
            {formatDateTime(item.saleDate, "dd/MM/yy") || "--/--/--"}
          </span>
          <span className="text-[15px] font-bold text-gray-400">
            {formatDateTime(item.saleDate, "HH:mm") || "--:--"}
          </span>
        </div>
      </div>

      <div className="flex-1 flex flex-col p-5 relative">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-[19px] font-bold text-[#334155] leading-tight">
            {item.clientName}
          </h3>

          <IconButton
            size="small"
            onClick={handleClick}
            className="text-gray-400 hover:text-gray-600 -mr-2 -mt-2"
          >
            <MoreVertical size={22} />
          </IconButton>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col">
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-1">
              Produto:
            </span>
            <span className="text-[15px] font-medium text-[#475569]">
              {item.productName}
            </span>
          </div>

          <div className="flex flex-col border-t border-gray-100 pt-3">
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-1">
              Tipo de Venda:
            </span>
            <span className="text-[15px] font-medium text-[#475569]">
              {item.saleTypeLabel || "Venda"}
            </span>
          </div>

          <div className="flex flex-col border-t border-gray-100 pt-3 gap-2">
            <div className="flex justify-between items-center">
              <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
                Valor Total:
              </span>
              <span className="text-[19px] font-bold text-claroprimaria-01">
                {formatCurrency(getSalePrice(item))}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
                Status:
              </span>
              <div
                className="px-3 py-1 rounded-full text-[12px] font-bold border"
                style={{
                  backgroundColor: `${getStatusColor(item.paymentStatus)}15`,
                  color: getStatusColor(item.paymentStatus),
                  borderColor: `${getStatusColor(item.paymentStatus)}30`,
                }}
              >
                {getStatusLabel(item.paymentStatus)}
              </div>
            </div>
          </div>
        </div>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          PaperProps={{
            elevation: 2,
            sx: {
              borderRadius: "12px",
              minWidth: "180px",
              mt: 1,
              "& .MuiMenuItem-root": {
                fontSize: "14px",
                fontWeight: 500,
                color: "#475569",
                gap: 1.5,
                py: 1.5,
              },
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem
            onClick={() => {
              navigate(`/sales/view/${item._id || item.id}`);
              handleClose();
            }}
          >
            <EyeIcon size={18} className="text-blue-500" />
            Ver Detalhes
          </MenuItem>
          <MenuItem
            onClick={() => {
              navigate(`/sales/edit/${item._id || item.id}`);
              handleClose();
            }}
          >
            <Edit size={18} className="text-amber-500" />
            Editar Venda
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleSendPayment(item);
              handleClose();
            }}
          >
            <Send size={18} className="text-emerald-500" />
            Enviar Link para Whatsapp
          </MenuItem>
          {item.paymentDetails?.method === "TED" && (
            <MenuItem
              onClick={() => {
                handleViewReceipt(item);
                handleClose();
              }}
            >
              <FileText size={18} className="text-purple-500" />
              Confirmar pagamento
            </MenuItem>
          )}
          <div className="h-px bg-gray-100 my-1 mx-2" />
          <MenuItem
            onClick={() => {
              handleDelete(item._id || item.id);
              handleClose();
            }}
            sx={{ color: "#ef4444 !important" }}
          >
            <Trash2Icon size={18} />
            Excluir
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
}

export function SalesList() {
  const [periodFilter, setPeriodFilter] = useState("Todos");
  const [statusFilter, setStatusFilter] = useState<PaymentStatus[]>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [receiptSale, setReceiptSale] = useState<any>(null);

  const handleViewReceipt = (sale: any) => {
    setReceiptSale(sale);
  };

  const filters = useMemo(() => {
    const f: ISaleFilters = {};
    if (statusFilter.length > 0) {
      f.paymentStatus = statusFilter;
    }
    if (periodFilter !== "Todos") {
      const now = new Date();
      if (periodFilter === "Hoje") {
        f.startDate = startOfDay(now).toISOString();
        f.endDate = endOfDay(now).toISOString();
      } else if (periodFilter === "Semana") {
        f.startDate = startOfWeek(now, { locale: ptBR }).toISOString();
        f.endDate = endOfWeek(now, { locale: ptBR }).toISOString();
      } else if (periodFilter === "Mes") {
        f.startDate = startOfMonth(now).toISOString();
        f.endDate = endOfMonth(now).toISOString();
      }
    }
    return f;
  }, [periodFilter, statusFilter]);

  const queryIndexSales = useIndexSales(filters);
  const mutationDeleteSale = useDeleteSale();
  const generateToken = useGenerateSaleToken();

  const getClientPhone = (client: any) => {
    if (!client) return "";
    if (typeof client === "object") {
      return client.whatsapp || client.celphone || "";
    }
    return "";
  };

  const handleSendPayment = (sale: any) => {
    const phone = getClientPhone(sale.clientId).replace(/\D/g, "");

    const newWindow = window.open("", "_blank");

    if (!newWindow) {
      toast.error(
        "O bloqueador de popups impediu a abertura do WhatsApp. Por favor, autorize popups.",
      );
      return;
    }

    const toastId = toast.loading("Gerando link de pagamento...");

    generateToken.mutate(
      {
        id: sale._id || sale.id,
        phone: phone || undefined,
      },
      {
        onSuccess: (response) => {
          if (response && newWindow) {
            const token = response.verificationToken?.token;
            const clientName = getClientName(sale.clientId);
            const productName = getProductName(sale.productId);
            const saleId = response._id || response.id;
            const paymentUrl = `${window.location.origin}/sales/payment/${saleId}`;

            const message = `Olá ${clientName}! Aqui está o link para o pagamento do produto *${productName}*:\n\n${paymentUrl}\n\nCódigo de acesso: *${token}*`;

            const cleanPhone =
              phone.length > 0
                ? phone.startsWith("55")
                  ? phone
                  : `55${phone}`
                : "";
            const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;

            newWindow.location.href = whatsappUrl;

            toast.success("Link enviado via WhatsApp!", { id: toastId });
          } else {
            newWindow?.close();
            toast.success("Token gerado com sucesso!", { id: toastId });
          }
        },
        onError: (err: any) => {
          newWindow?.close();
          toast.error(err.message || "Erro ao gerar token de pagamento.", {
            id: toastId,
          });
        },
      },
    );
  };

  const extractName = (data: any) => {
    if (!data) return null;
    let name = "";
    if (typeof data === "object") {
      name = data.name;
    } else if (typeof data === "string") {
      const match = data.match(/name:\s*'([^']+)'/);
      name = match ? match[1] : data;
    }

    if (!name) return null;

    return name
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getClientName = (client: any) => {
    return extractName(client) || "Cliente não encontrado";
  };

  const getProductName = (product: any) => {
    return extractName(product) || "Produto não encontrado";
  };

  const filteredSales = useMemo(() => {
    return queryIndexSales.data || [];
  }, [queryIndexSales.data]);

  const tableData = useMemo(() => {
    return filteredSales.map((sale) => {
      const client = sale.clientId;
      const avatarUrl =
        typeof client === "object" && client?.photo
          ? client.photo
          : `https://ui-avatars.com/api/?name=${encodeURIComponent(
              getClientName(sale.clientId),
            )}&background=random`;

      return {
        ...sale,
        avatarUrl,
        clientName: getClientName(sale.clientId),
        productName: getProductName(sale.productId),
        saleTypeLabel: sale.saleType || "",
        price: sale.paymentDetails?.value || getSalePrice(sale),
      };
    });
  }, [filteredSales]);

  const columns: Column<(typeof tableData)[0]>[] = [
    {
      image: "avatarUrl",
      key: "clientName",
      header: "Cliente",
      width: 280,
      render: (_, item) => (
        <div className="flex flex-col justify-center gap-1">
          <span className="font-semibold text-gray-900 leading-tight text-[15px]">
            {item.clientName}
          </span>
          <span className="text-[11px] text-gray-600 uppercase font-medium tracking-wide">
            {item.productName}
          </span>
          <span className="text-[10px] text-blue-500 font-bold uppercase">
            {item.saleTypeLabel}
          </span>
        </div>
      ),
    },
    {
      key: "saleDate",
      header: "Data de Venda",
      width: 150,
      render: (_, item) => (
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <Calendar size={14} />
          {item.saleDate
            ? format(new Date(item.saleDate), "dd/MM/yy HH:mm", {
                locale: ptBR,
              })
            : "Venda não realizada"}
        </div>
      ),
    },
    {
      key: "price",
      header: "Valor",
      width: 150,
      render: (price) => (
        <div className="text-base font-bold text-gray-900">
          {formatCurrency(price)}
        </div>
      ),
    },
    {
      key: "paymentStatus",
      header: "Status",
      width: 180,
      render: (_, item) => (
        <div
          className="px-3 py-1 rounded-full text-[12px] font-bold border inline-block"
          style={{
            backgroundColor: `${getStatusColor(item.paymentStatus)}15`,
            color: getStatusColor(item.paymentStatus),
            borderColor: `${getStatusColor(item.paymentStatus)}30`,
          }}
        >
          {getStatusLabel(item.paymentStatus)}
        </div>
      ),
    },
  ];

  const handleDelete = (id: string) => {
    setDeleteId(id);
  };

  const confirmDelete = () => {
    if (deleteId) {
      mutationDeleteSale.mutate(deleteId, {
        onSuccess: () => {
          setDeleteId(null);
        },
        onError: () => {
          toast.error("Erro ao excluir venda.");
        },
      });
    }
  };

  return (
    <div className="flex flex-col h-full bg-white p-6">
      <DataTable
        data={tableData}
        columns={columns}
        renderItem={(item) => (
          <SalesCard
            item={item}
            handleSendPayment={handleSendPayment}
            handleDelete={handleDelete}
            handleViewReceipt={handleViewReceipt}
          />
        )}
        newFormat
        rowHeight={90}
        basePath="sales"
        loading={queryIndexSales.isPending}
        title="Vendas"
        onDelete={handleDelete}
        actions={[
          {
            label: "Enviar fluxo de pagamento no WhatsApp",
            icon: <Send size={16} />,
            onClick: handleSendPayment,
          },
          {
            label: "Confirmar pagamento",
            icon: <CheckCircle size={16} />,
            onClick: handleViewReceipt,
            disabled: (item: any) => item.paymentDetails?.method !== "TED",
          },
        ]}
        children={() => (
          <SalesFilters
            periodFilter={periodFilter}
            setPeriodFilter={setPeriodFilter}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
          />
        )}
      />

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja excluir esta venda?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancelar</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <SalesReceiptModal
        open={!!receiptSale}
        onClose={() => setReceiptSale(null)}
        sale={receiptSale}
      />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={generateToken.isPending}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
}
